
// Product Schema
export function schemaProductList(products) {
    const itemListElement = products.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://ts-eu24-develop.ventrox.com/${product.slug}/p${product.code}`,
        item: schemaProduct(product)
    }));

    useSchemaOrg({
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement
    });
}

export function schemaProduct(product) {
    const schema = {
        "@type": "Product",
        name: product.name,
        description: product.description,
        brand: schemaBrand(product.brand),
        image: product.image,
        sku: product.code,
        offers: schemaOffers(product.prices, product.stockDetails)
    };

    if(product.prices){
        schema.offers= schemaOffers(product.prices, product.stockDetails);
    }

    // Add technical specifications if available
    if (product.technical_spec_attributes.length) {
        schema.additionalProperty = schemaPropertyValues(product.technical_spec_attributes);
    }

    // Add aggregate rating only if both rating and review count exist
    if (product.other_attributes) {
        const aggregateRating = schemaAggregateRating(product.other_attributes);
        if (aggregateRating) {
            schema.aggregateRating = aggregateRating;
        }
    }

    return schema;
}

function schemaBrand(brandName) {
    return {
        "@type": "Brand",
        name: brandName
    };
}

function schemaAggregateRating(otherAttributes) {
    const rating = otherAttributes?.find(attr => attr.key === "Product Review Rating");
    const reviews = otherAttributes?.find(attr => attr.key === "Number of Reviews");

    if (rating?.value && reviews?.value) {
        const ratingValue = parseFloat(rating.value);
        const reviewCount = parseInt(reviews.value, 10);
        
        if (!isNaN(ratingValue) && !isNaN(reviewCount) && reviewCount > 0) {
            return {
                "@type": "AggregateRating",
                ratingValue: ratingValue,
                reviewCount: reviewCount
            };
        }
    }
    return null;
}

function schemaOffers(prices, stockDetails) {
    if(!stockDetails){
        return null;
    }
    return {
        "@type": "Offer",
        price: parseFloat(prices.raw.gross),
        priceCurrency: "EUR",
        availability: stockDetails.delivery > 0 
            ? "https://schema.org/InStock" 
            : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition"
    };
}

function schemaPropertyValues(attributes) {
    return attributes.map(({ key, value }) => ({
        "@type": "PropertyValue",
        name: key,
        value: value.toString()
    }));
}

// Breadcrumb Schema
export function schemaBreadcrumb(breadcrumbs) {
    const breadcrumbList = breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.text,
        item: crumb.link
    }));

    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbList
    };
    useSchemaOrg([schema]);
    return schema;
}

export function schemaPDP(product) {

    let productObject = {
        '@type': 'Product',
        name: product?.name || "",
        description: product.description || "",
        sku: product.code,
        url: `/${product.slug}/p${product.code}`,
    };

    if(product.images){
        productObject.image = [
            product.images.default,
            product.images.big,
            product.images.biggest,
        ];
    }

    if(product.brand){
        productObject.brand = {
            '@type': 'Brand',
                name: product.brand,
        };
    }

    if(product.prices){
        productObject.offers = {
            '@type': 'Offer',
                priceCurrency: 'EUR',
                price: product.prices?.raw.gross.toFixed(2),
                itemCondition: 'https://schema.org/NewCondition',
                availability: product.next_day_delivery
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
                seller: {
                '@type': 'Organization',
                    name: 'Tool Station',
            },
        };
    }

    if(product.technical_spec_attributes){
        productObject.gtin= product.technical_spec_attributes.find(attr => attr.key === 'EAN')?.value;

        productObject.additionalProperty= product.technical_spec_attributes.map(attr => ({
            '@type': 'PropertyValue',
            name: attr.key,
            value: attr.value,
        }));
    }

    const productSchema = defineProduct(productObject);
    useSchemaOrg([productSchema]);
    return productSchema;
  }

export function schemaHomePage(route, siteBaseUrl) {
    const baseUrl = `${siteBaseUrl}${route?.path || "/"}`;
    const schemaData = {
      organizationName: "Toolstation",
      legalName: "Toolstation",
      socialSites: [
        "https://www.linkedin.com/company/toolstation-nl",
        "https://www.youtube.com/channel/UCT5P5fy539NUKmHX8gOOpAQ",
        "https://www.facebook.com/ToolstationNederland",
        "https://www.instagram.com/toolstation_nl",
        "https://x.com/toolstaionnl",
      ],
      description:
        "Best tools & building supplies for the professional. More than 15,000 products in stock. Lowest prices and fastest service. Only at Toolstation!",
      contactPoint: {
        telephone: "63449595",
        contactType: "Customer Service",
        areaServed: "NL",
        availableLanguage: "Dutch",
      },
      logoUrl: `${siteBaseUrl}/images/logos/toolstation-logo-halo.svg`,
    };
  
    // Organization schema
    const organizationSchema = {
      "@type": "Organization",
      "@id": `${baseUrl}#identity`,
      name: schemaData.organizationName,
      legalName: schemaData.legalName,
      url: siteBaseUrl,
      logo: {
        "@id": `${baseUrl}#logo`,
      },
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: schemaData.contactPoint.telephone,
          contactType: schemaData.contactPoint.contactType,
          areaServed: schemaData.contactPoint.areaServed,
          availableLanguage: schemaData.contactPoint.availableLanguage,
        },
      ],
      sameAs: schemaData.socialSites,
    };
  
    // WebPage schema
    const webpageSchema = {
      "@type": "WebPage",
      "@id": `${baseUrl}#webpage`,
      name: `${schemaData.organizationName} | Home`,
      url: baseUrl,
      description: schemaData.description,
      about: {
        "@id": `${baseUrl}#identity`,
      },
      isPartOf: {
        "@id": `${baseUrl}#website`,
      },
      primaryImageOfPage: {
        "@id": `${baseUrl}#logo`,
      },
      potentialAction: [
        {
          "@type": "ReadAction",
          target: [baseUrl],
        },
      ],
    };
  
    // Website schema
    const websiteSchema = {
      "@type": "WebSite",
      "@id": `${baseUrl}#website`,
      name: schemaData.organizationName,
      url: siteBaseUrl,
      inLanguage: "nl-NL",
      publisher: {
        "@id": `${baseUrl}#identity`,
      },
    };
  
    // Logo schema
    const logoSchema = {
      "@type": "ImageObject",
      "@id": `${baseUrl}#logo`,
      caption: schemaData.organizationName,
      contentUrl: schemaData.logoUrl,
      inLanguage: "nl-NL",
      url: schemaData.logoUrl,
    };
    useSchemaOrg([organizationSchema, webpageSchema, websiteSchema, logoSchema]);
  }
  
  // FAQs Schema
  export function schemaFAQs(faqs) {
     const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.heading,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.content,
          },
        })),
      };
    useSchemaOrg([faqSchema]);
    return faqSchema;
  }

  export function schemaRating(rating: any | null, reviewCount: any | null){
    if (!rating && !reviewCount) {
      return null;
    }
    const aggregateRating = {
      "@type": "AggregateRating",
    };
    if (rating) {
      aggregateRating.ratingValue = parseFloat(rating).toFixed(1);
    }
    if (reviewCount) {
      aggregateRating.reviewCount = reviewCount;
    }
    return aggregateRating;
  };

  export const schemaPropertyValue = (name: string, value: any) => {
    if (name && value !== undefined && value !== null) {
      return {
        "@type": "PropertyValue",
        name,
        value,
      };
    }
    return null;
  };
  
  export const schemaGeoCoordinates = (latitude: string, longitude: string) => {
    if (latitude && longitude) {
      return {
        "@type": "GeoCoordinates",
        latitude,
        longitude,
      };
    }
    return null;
  };

  export function schemaBranch(branchData: any, productRating: any = null, productReviews: any = null) {
    const schema: any = {
        "@type": "LocalBusiness",
    };

    if (branchData.id) {
        const branchIdUrl = `https://example.com/branches/${branchData?.name}`;
        schema["@id"] = branchIdUrl;
        schema.branchCode = branchData.id;
    }

    if (branchData.name) schema.name = branchData.name;

    if (branchData.address) {
        schema.address = {
            "@type": "PostalAddress",
            "streetAddress": branchData.address[0] || '',
            "postalCode": branchData.postcode || '',
            "addressLocality": branchData.address[1] || '',
            "addressCountry": branchData.address[3] || '',
        };
    }

    if (branchData.contact_number) schema.telephone = branchData.contact_number;
    if (branchData.details) {
        schema.description = branchData.details.replace(/<br\s*\/?>/g, "\n").trim();
    }

    const geo = schemaGeoCoordinates(
        parseFloat(branchData.geolocation?.latitude),
        parseFloat(branchData.geolocation?.longitude)
    );
    if (geo) schema.geo = geo;

    const additionalProperties = [];
    const clickAndCollect = schemaPropertyValue("Click and Collect Available", branchData.click_and_collect_available);
    const parking = schemaPropertyValue("Parking Available", branchData.parking_available);

    if (clickAndCollect) additionalProperties.push(clickAndCollect);
    if (parking) additionalProperties.push(parking);
    if (additionalProperties.length > 0) schema.additionalProperty = additionalProperties;

    if (productReviews !== null || productRating !== null) {
        schema.aggregateRating = schemaRating(productRating, productReviews);
    }
    useSchemaOrg([schema])
    return schema;
}

export function schemaBranchListing(branchesData: any) {
    const branches: any = [];
    if (!branchesData || typeof branchesData !== "object") return;
    let position = 1;
    Object.keys(branchesData).forEach((letter) => {
        const branchArray = branchesData[letter];
        if (!Array.isArray(branchArray) || branchArray.length === 0) return;

        branchArray.forEach((branchData: any) => {
            const branchSchema = schemaBranch(branchData);
            if (branchSchema) {
              branchSchema.position = position++;
              branches.push(branchSchema);
            }
        });
    });

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "CollectionPage",
                "name": "Store Branches",
                "hasPart": branches,
            },
        ],
    };
    useSchemaOrg(schema);
    return schema;
  }
export function schemaBrands(brandData: any): Record<string, any> {
  const schema: Record<string, any> = {
    "@type": "Brand",
    "@id": `https://example.com/brand/${brandData.id}`,
    "name": brandData.name,
    "logo": brandData.logo,
  };

  if (brandData.description) schema.description = brandData.description;
  if (brandData.website) schema.url = brandData.website;
  if (brandData.foundedYear) schema.foundingDate = brandData.foundedYear.toString();

  return schema;
}

export function schemaBrandListing(brandsData: any[]): Record<string, any> {
  if (!brandsData || !Array.isArray(brandsData)) return null;

  const brands = brandsData.map((brand) => ({
    "@type": "ListItem",
    "position": brandsData.indexOf(brand) + 1,
    "item": schemaBrands(brand),
  }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Brand Listing",
    "itemListElement": brands,
    "numberOfItems": brands.length,
  };
  useSchemaOrg(schema)
  return schema;
}
  function schemaAuthor(author: any) {
    if(author && author.data) {
    return {
      "@type": "Person",
      name: author.data.name || '',
      description: author.data.summary || '', // Use author's summary for description
      url: `https://your-domain.com/authors/${author.id}` // Construct URL dynamically
    };
  }
  }
  
  function schemaPublisher() {
    return {
      "@type": "Organization",
      name: "toolstation", // Your organization's name
      logo: {
        "@type": "ImageObject",
        url: "https://your-domain.com/logo.png"
      }
    };
  }
  
  function estimateWordCount(post: any): number {
    const content = extractArticleBody(post);
    return content ? content.split(/\s+/).length : 0;
  }
  
  function extractArticleBody(post: any): string {
    return post.data.blocks
      .filter(block => block.component?.name === "Typography")
      .map(block => block.component.options?.text || "")
      .join(" ");
  }

  export function generateSchemaList(items, itemType, schemaType, options = {}) {
    try {
      const itemListElement = items.map((item, index) => {
        const schema = generateSchemaItem(item, schemaType, options);
  
        return {
          "@type": "ListItem",
          position: index + 1,
          url: `https://your-domain.com${item?.data?.url || ""}`,
          item: schema,
        };
      });
  
      useSchemaOrg({
        "@context": "https://schema.org",
        "@type": itemType || "ItemList",
        itemListElement,
      });
    } catch (error) {
      console.error("Error in generateSchemaList:", error);
    }
  }
  
  export function generateSchemaItem(item, schemaType, { imageField = "image", additionalFields = {} } = {}) {
    try {
      const { data = {}, lastUpdated } = item || {};
      const {
        title = "Untitled",
        excerpt = "",
        publishDate,
        url = "",
        tags = [],
        categories = {},
        chooseAuthor = { value: "" },
      } = data;
  
      return {
        "@type": schemaType,
        headline: title,
        description: excerpt,
        image: data[imageField] || "",
        datePublished: publishDate ? new Date(publishDate).toISOString() : undefined,
        dateModified: lastUpdated ? new Date(lastUpdated).toISOString() : undefined,
        author: schemaAuthor(chooseAuthor?.value),
        publisher: schemaPublisher(),
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://your-domain.com${url}`,
        },
        keywords: tags.join(", "),
        wordCount: estimateWordCount(item), 
        articleSection: categories?.value?.name || "Uncategorized",
        ...additionalFields,
      };
    } catch (error) {
      console.error("Error in generateSchemaItem:", error);
      return {};
    }
  }
  
  export function generateContentSchema(item, schemaType, options = {}) {
    const {
      titleField = "title",
      descriptionField = "excerpt",
      imageField = "image",
      tagsField = "tags",
      categoryField = "categories",
    } = options;

    if(!item){
        return;
    }
  
    const { data = {}, lastUpdated } = item || {};
  
    const {
      [titleField]: title = "Untitled",
      [descriptionField]: description = "",
      [imageField]: image = "",
      publishDate,
      url = "",
      chooseAuthor = { value: { data: {} } },
      [tagsField]: tags = [],
      [categoryField]: categories = {},
    } = data;
  
    const authorData = chooseAuthor?.value?.data || {};
  
    const schema = {
      "@context": "https://schema.org",
      "@type": schemaType,
      headline: title,
      description,
      image,
      datePublished: publishDate ? new Date(publishDate).toISOString() : undefined,
      dateModified: lastUpdated ? new Date(lastUpdated).toISOString() : undefined,
      author: authorData,
      publisher: schemaPublisher(),
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://your-domain.com${url}`,
      },
      keywords: tags.join(", "),
      wordCount: estimateWordCount(item), 
      articleSection: categories?.value?.data?.name || "",
    };
  
    useSchemaOrg([schema]);
    return schema;
  }
  
